import { useEffect, useRef, useState } from "react"

// Animated mini-mockup for the Knowledge feature row.
// 9s loop: static library (Case Studies > Highways selected) → folder switches to Methodology → hold.
// Styles live under `.kn-*` classes in src/index.css (scoped to .landing-page).

type FileExt = "pdf" | "docx" | "xlsx"

type DocItem = {
  name: string
  ext: FileExt
  category: string
  size: string
  date: string
}

const HIGHWAY_DOCS: DocItem[] = [
  { name: "M5 Bridge Upgrade Programme",   ext: "pdf",  category: "Case Study", size: "4.2 MB", date: "12 Apr" },
  { name: "Northbridge Tunnel Project",    ext: "pdf",  category: "Case Study", size: "2.1 MB", date: "03 Mar" },
  { name: "Coastal Highway Expansion",     ext: "docx", category: "Case Study", size: "1.8 MB", date: "19 Feb" },
  { name: "Westcoast Arterial Phase 2",    ext: "pdf",  category: "Case Study", size: "3.0 MB", date: "27 Jan" },
  { name: "Greenway Bypass Construction",  ext: "pdf",  category: "Case Study", size: "1.4 MB", date: "14 Jan" },
]

const METHODOLOGY_DOCS: DocItem[] = [
  { name: "Highway Construction Methodology v3", ext: "docx", category: "Methodology", size: "1.6 MB", date: "22 Mar" },
  { name: "HSE Procedures Manual",               ext: "pdf",  category: "Methodology", size: "2.4 MB", date: "18 Mar" },
  { name: "Quality Assurance Framework",         ext: "docx", category: "Methodology", size: "1.1 MB", date: "09 Feb" },
  { name: "Subcontractor Management v2",         ext: "pdf",  category: "Methodology", size: "890 KB", date: "04 Feb" },
]

function FolderIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M2 4a1 1 0 0 1 1-1h3.5l1 1H13a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4z" />
    </svg>
  )
}

function ChevIcon() {
  return (
    <svg viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3,2 7,5 3,8" />
    </svg>
  )
}

function ExtLabel({ ext }: { ext: FileExt }) {
  return ext === "docx" ? <>DOC</> : ext === "xlsx" ? <>XLS</> : <>PDF</>
}

function DocRow({ doc }: { doc: DocItem }) {
  return (
    <div className="kn-doc-row">
      <div className="kn-doc-name">
        <span className={`kn-file-ico kn-${doc.ext}`}><ExtLabel ext={doc.ext} /></span>
        <span className="kn-doc-name-text">{doc.name}</span>
      </div>
      <span className="kn-doc-size">{doc.size}</span>
      <span className="kn-doc-date">{doc.date}</span>
    </div>
  )
}

function PlainFolder({ name, count }: { name: string; count: number }) {
  return (
    <div className="kn-folder">
      <span className="kn-chev"><ChevIcon /></span>
      <span className="kn-folder-icon"><FolderIcon /></span>
      <span className="kn-folder-name">{name}</span>
      <span className="kn-folder-count">{count}</span>
    </div>
  )
}

function ChildFolder({ name, count, extraClass = "" }: { name: string; count: number; extraClass?: string }) {
  return (
    <div className={`kn-folder kn-indent kn-no-chev ${extraClass}`}>
      <span className="kn-chev" />
      <span className="kn-folder-icon"><FolderIcon /></span>
      <span className="kn-folder-name">{name}</span>
      <span className="kn-folder-count">{count}</span>
    </div>
  )
}

export function KnowledgeVisual() {
  const rootRef = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      (entries) => setInView(entries[0]?.isIntersecting ?? false),
      { threshold: 0.4 },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  return (
    <div ref={rootRef} className={`kn-frame ${inView ? "kn-playing" : ""}`}>
      <div className="kn-head">
        <span className="kn-head-title">Knowledge Library</span>
        <span className="kn-head-meta">183 documents</span>
      </div>

      <div className="kn-body">
        {/* Sidebar */}
        <div className="kn-sidebar">
          <div className="kn-sidebar-label">My Folders</div>

          {/* Case Studies parent + children */}
          <div className="kn-folder kn-cs-parent kn-expanded">
            <span className="kn-chev"><ChevIcon /></span>
            <span className="kn-folder-icon"><FolderIcon /></span>
            <span className="kn-folder-name">Case Studies</span>
            <span className="kn-folder-count">12</span>
          </div>
          <div className="kn-children kn-cs-children">
            <ChildFolder name="Highways" count={5} extraClass="kn-highways" />
            <ChildFolder name="Rail" count={4} />
            <ChildFolder name="Energy" count={3} />
          </div>

          {/* Methodology parent + children */}
          <div className="kn-folder kn-meth-parent">
            <span className="kn-chev"><ChevIcon /></span>
            <span className="kn-folder-icon"><FolderIcon /></span>
            <span className="kn-folder-name">Methodology</span>
            <span className="kn-folder-count">8</span>
          </div>
          <div className="kn-children kn-meth-children">
            <ChildFolder name="Highway Specs" count={3} />
            <ChildFolder name="HSE Procedures" count={2} />
          </div>

          {/* Other top-level folders */}
          <PlainFolder name="Certifications" count={6} />
          <PlainFolder name="CVs" count={38} />
          <PlainFolder name="Past Proposals" count={24} />
          <PlainFolder name="Pricing" count={12} />
        </div>

        {/* Main panel */}
        <div className="kn-main">
          <div className="kn-breadcrumb">
            <div className="kn-bc-state kn-bc-cs">
              <span>Case Studies</span>
              <span className="kn-bc-sep">›</span>
              <span className="kn-bc-current">Highways</span>
            </div>
            <div className="kn-bc-state kn-bc-meth">
              <span className="kn-bc-current">Methodology</span>
            </div>
          </div>

          <div className="kn-table-head">
            <span>Name</span>
            <span>Size</span>
            <span>Modified</span>
          </div>

          <div className="kn-table-wrap">
            <div className="kn-doc-list">
              <div className="kn-doc-list-group kn-docs-hw">
                {HIGHWAY_DOCS.map((d) => <DocRow key={d.name} doc={d} />)}
              </div>
              <div className="kn-doc-list-group kn-docs-meth">
                {METHODOLOGY_DOCS.map((d) => <DocRow key={d.name} doc={d} />)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
